import { CircleCheck, X } from 'lucide-react';

import { CustomInput, CustomSelectPicker, CustomTextArea, Loader } from '#/common/components';
import { COLORS, FIELD_TYPES, MAX_LENGTH_LIMIT } from '#/utils/constants';
import { capitalizeText, getFieldPlaceholder, getSelectPickerValue } from '#/utils/helperFunctions';
import { RenderFileList } from '..';

function LeadForm({
  lead,
  leadFormik,
  handleFileChange,
  handleTelInputChange,
  handleLeadFormClose,
  isFieldSubmit,
  isUploadFileLoading,
  isProcessingFiles,
  fileRef,
  uploadedFiles,
  color,
  textColor,
  handleRemoveFile,
  handleSelectChange,
  isLeadSubmitLoading,
  isSubmissionSuccess,
}) {
  const isLoading = isUploadFileLoading || isProcessingFiles || isLeadSubmitLoading || isSubmissionSuccess;

  const getFieldOnChangeHandler = (fieldType, label) => {
    if (fieldType === FIELD_TYPES.UPLOAD) {
      return handleFileChange(label);
    }

    if (fieldType === FIELD_TYPES.NUMBER) {
      return handleTelInputChange(label);
    }

    return leadFormik.handleChange;
  };

  const getKeyPressHandler = fieldType => {
    if (fieldType === FIELD_TYPES.NUMBER) {
      return e => {
        if (!/\d/.test(e.key)) {
          e.preventDefault();
        }
      };
    }

    return undefined;
  };

  const { name = '', fields = [], submitButtonText = '' } = lead || {};

  const leadSubmitButtonText = isSubmissionSuccess ? (
    <CircleCheck />
  ) : isLeadSubmitLoading ? (
    <Loader />
  ) : (
    submitButtonText
  );

  return (
    <div className="chatbot-lead">
      <div className="head">
        <h4>{name}</h4>
        <span onClick={handleLeadFormClose}>
          <X />
        </span>
      </div>
      <form onSubmit={leadFormik.handleSubmit} className="fields">
        {fields?.map((field, index) => {
          if (!field?.label) return null;

          const { label, type, fieldItems = [], isRequired, optionalLabelText, isMultiSelect } = field;
          const fieldType = type?.toLowerCase();
          const isFileField = fieldType === FIELD_TYPES.UPLOAD;
          const isNumberField = fieldType === FIELD_TYPES.NUMBER;
          const isDropDownField = fieldType === FIELD_TYPES.DROPDOWN;
          const isTextAreaField = fieldType === FIELD_TYPES.TEXTAREA;
          const fieldError = leadFormik.errors?.[label] && leadFormik.touched?.[label];

          return (
            <div key={`${label}-${index}`} className="form-field">
              {isDropDownField ? (
                <CustomSelectPicker
                  name={label}
                  value={getSelectPickerValue(label, isMultiSelect, fieldItems, leadFormik.values)}
                  onChange={value => handleSelectChange(label, value)}
                  options={fieldItems}
                  placeholder={capitalizeText(label)}
                  isError={fieldError}
                  errorMessage={leadFormik.errors?.[label]}
                  isRequired={isRequired}
                  optionalLabelText={optionalLabelText}
                  isMultiple={isMultiSelect}
                />
              ) : isTextAreaField ? (
                <CustomTextArea
                  name={label}
                  value={leadFormik.values?.[label] || ''}
                  onChange={leadFormik.handleChange}
                  placeholder={capitalizeText(label)}
                  isRequired={isRequired}
                  optionalLabelText={optionalLabelText}
                  isError={fieldError}
                  errorMessage={leadFormik.errors?.[label]}
                />
              ) : (
                <div className="field-wrapper">
                  <CustomInput
                    key={isFileField ? (isFieldSubmit ? 'uploaded' : 'empty') : label}
                    type={isFileField ? FIELD_TYPES.FILE : isNumberField ? FIELD_TYPES.TEL : fieldType}
                    name={label}
                    placeholder={getFieldPlaceholder(fieldType, label, isProcessingFiles)}
                    multiple={isFileField}
                    ref={fileRef}
                    isRequired={isRequired}
                    optionalLabelText={optionalLabelText}
                    onChange={getFieldOnChangeHandler(fieldType, label)}
                    maxLength={MAX_LENGTH_LIMIT}
                    value={isFileField ? undefined : leadFormik.values?.[label] || ''}
                    isError={fieldError}
                    errorMessage={leadFormik.errors?.[label]}
                    onKeyPress={getKeyPressHandler(fieldType)}
                    disabled={isFileField && isProcessingFiles}
                  />
                  {isFileField && uploadedFiles?.[label] && (
                    <RenderFileList
                      files={uploadedFiles[label]}
                      fieldName={label}
                      handleRemoveFile={handleRemoveFile}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
        <button
          type="submit"
          className="btn btn-primary"
          style={{
            '--chatbot-bg': isSubmissionSuccess ? COLORS.SUCCESS : color,
            '--chatbot-text-color': textColor,
          }}
          disabled={isLoading}
        >
          {leadSubmitButtonText}
        </button>
      </form>
    </div>
  );
}

export default LeadForm;
